import ActivityFeed from "@/components/conduction-social/ActivityFeed";
import ActivityFeedSidebar from "@/components/conduction-social/ActivityFeedSidebar";
import LoginModal from "@/components/modals/LoginModal";
import { SessionContext } from "@/context/SessionContext";
import {
    ActionIcon,
    Box,
    Card,
    Flex,
    Text,
    useMantineTheme
} from "@mantine/core";
import { IconChevronRight, IconX } from "@tabler/icons-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/* ------------------------------ types/contract ------------------------------ */
/** 
 * Prop contract expected by your existing ActivityFeed component.
 * If your ActivityFeed already uses a different prop shape, adapt the mapping below.
 */
export type ActivityFeedMode = "home" | "classroom" | "user" | "me";

export interface ActivityFeedPropsForWrapper {
    mode: ActivityFeedMode;
    classroomId?: string;
    userId?: string;
}

/* --------------------------------- component -------------------------------- */

const SIDEBAR_WIDTH = 300;

const ActivityFeedPage: React.FC = () => {
    const theme = useMantineTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const session = useContext(SessionContext);
    if (!session) throw new Error("ActivityFeedPage must be a child of SessionContext.");

    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
	const [showLoginModal, setShowLoginModal] = useState<boolean>(true);

    // Determine mode + IDs from the current route
    // Supported routes:
    //  - /activity-feed                       -> mode: "home"
    //  - /classroom/:classroomId/activity-feed -> mode: "classroom"
    //  - /user/:userId/activity-feed           -> mode: "me" or "user" depending on userId
    const { mode, classroomId, userId } = useMemo(() => {
        const path = location.pathname.toLowerCase();

        if (path.startsWith("/classroom/")) {
            const cid = params.classroomId ?? session.user?.classroomId ?? undefined;
            return { mode: "classroom" as const, classroomId: cid, userId: undefined };
        }

        if (path.startsWith("/user/")) {
            const uid = params.userId ?? session.user?.id ?? undefined;
            if (uid === session.user?.id) {
                return { mode: "me" as const, classroomId: undefined, userId: uid };
            }
            return { mode: "user" as const, classroomId: undefined, userId: uid };
        }

        return { mode: "home" as const, classroomId: undefined, userId: undefined };
    }, [location.pathname, params.classroomId, params.userId, session.user]);

    useEffect(() => {
        // Scroll to top when the location changes
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: "instant" });
        }
    }, [location.pathname]);

    // Handlers for sidebar clicks -> push route
    const goHome = () => navigate("/activity-feed");
    const goClassroom = () => {
        const cid = session.user?.classroomId;
        if (cid) navigate(`/classroom/${cid}/activity-feed`);
        else navigate("/activity-feed");
    };
    const goMyFeed = () => {
        const uid = session.user?.id;
        if (uid) navigate(`/user/${uid}/activity-feed`);
        else navigate("/activity-feed");
    };
    const goToUserFeed = (uid: string) => {
        if (uid) navigate(`/user/${uid}/activity-feed`);
        else navigate("/activity-feed");
    };

	if (session.user == null) {
		return (
			<>
				<LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
				<Card p={20}>
					<Text>Log in to watch videos.</Text>
				</Card>
			</>
		);
	}

    return (
        <Flex w="100%" h="100%" style={{ overflow: "hidden", position: "relative" }}>
            {/* Sidebar */}
            <Box
                style={{
                    position: "fixed",
                    left: isSidebarOpen ? 0 : -SIDEBAR_WIDTH,
                    top: 40,
                    width: SIDEBAR_WIDTH,
                    height: "100%",
                    backgroundColor: theme.colors.blue[6],
                    overflowY: "auto",
                    zIndex: 100,
                    transition: "left 0.3s ease",
                }}
            >
                {/* Close button inside sidebar */}
                {isSidebarOpen && (
                    <ActionIcon
                        onClick={() => setIsSidebarOpen(false)}
                        style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            zIndex: 110,
                            color: theme.white,
                        }}
                        size="md"
                        variant="transparent"
                    >
                        <IconX />
                    </ActionIcon>
                )}

                <ActivityFeedSidebar
                    currentMode={mode}
                    onHome={goHome}
                    onClassroom={goClassroom}
                    onMyFeed={goMyFeed}
                    hasClassroom={!!session.user?.classroomId}
                />
            </Box>

            {/* Open button when sidebar is closed */}
            {!isSidebarOpen && (
                <ActionIcon
                    onClick={() => setIsSidebarOpen(true)}
                    style={{
                        position: "fixed",
                        left: 0,
                        top: 60,
                        zIndex: 4,
                        backgroundColor: theme.colors.blue[7],
                        color: theme.white,
                    }}
                    size="lg"
                >
                    <IconChevronRight />
                </ActionIcon>
            )}

            {/* Main content */}
            <Box
                ref={scrollContainerRef}
                style={{
                    marginLeft: isSidebarOpen ? SIDEBAR_WIDTH : 0,
                    flex: 1,
                    height: "100%",
                    overflowY: "auto",
                    transition: "margin-left 0.3s ease",
                }}
            >
                <Box w="100%" mt="sm">
                    <ActivityFeed
                        classroomId={classroomId}
                        userId={userId}
                        goToUserFeed={goToUserFeed}
                    />
                </Box>
            </Box>
        </Flex>
    );
};

export default ActivityFeedPage;
